import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import SelectDialog, {
  SelectDialogSearchField,
  SelectDialogSubmitButton,
  SelectDialogTable
} from '../../../common/dialogs/SelectDialog';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import ProductAutocomplete from '../components/ProductAutocomplete';
import {
  useFindProductVerifications,
  useProductEntityPrefix
} from '../Product.hooks';
import {
  BaseProductAssignDialogProps,
  ProductVerification
} from '../Product.interface';

export interface AssignVerificationDialogProps
  extends Omit<
    BaseProductAssignDialogProps<never, ProductVerification>,
    'onAssigned' | 'productId'
  > {
  productId?: number;
  title?: string;
  contentText?: string;
  isLoading?: boolean;
  defaultLimit?: number;
  onSubmit?: (verifications: ProductVerification[]) => void;
}

const AssignVerificationDialog: React.FC<AssignVerificationDialogProps> = (
  props: AssignVerificationDialogProps
) => {
  const {
    open,
    selectedItems,
    isLoading = false,
    defaultLimit = 5,
    title = 'Assign Verifications',
    contentText = 'Search for Verifications to assign.',
    onCancel,
    onError,
    onSubmit
  } = props;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);
  const [productId, setProductId] = useState(props.productId);
  const {
    productVerificationList,
    isLoadingProductVerifications,
    loadingProductVerificationsError,
    findProductVerifications
  } = useFindProductVerifications(productId || 0);
  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.Verification
  );

  // handle searching
  useEffect(() => {
    const params: ApiParams = { search, page, limit };
    findProductVerifications(params);
  }, [search, limit, page, productId, findProductVerifications]);

  useEffect(() => {
    if (loadingProductVerificationsError) {
      onError?.(loadingProductVerificationsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingProductVerificationsError]);

  return (
    <SelectDialog open={open} onClose={onCancel} selectedItems={selectedItems}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{contentText}</DialogContentText>
        <SelectDialogSearchField
          label='Verification'
          onSearch={(value: string) => {
            setPage(0);
            setSearch(value);
          }}
        />
        <Box mt={1} mb={2} className='search-box'>
          <ProductAutocomplete
            productId={productId}
            onChange={product => {
              if (product) {
                setProductId(product.id);
              }
            }}
          />
        </Box>
        <SelectDialogTable
          multiple
          idKey='id'
          columns={[
            {
              key: 'id',
              label: 'ID',
              render: (item: ProductVerification) => prefix + item.id
            },
            { key: 'title', label: 'Title', wrap: true },
            {
              key: 'task.status',
              label: 'Status',
              render: (item: ProductVerification) => {
                return item.task && <TaskStatusLabel task={item.task} />;
              }
            }
          ]}
          isLoading={isLoadingProductVerifications || isLoading}
          dataList={productVerificationList}
          page={page}
          rowsPerPage={limit}
          onChangePage={page => setPage(page)}
          onChangeRowsPerPage={limit => setLimit(limit)}
        />
        {loadingProductVerificationsError && (
          <Alert severity='error'>
            Error getting data. Please check your internet connection and try
            again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <SelectDialogSubmitButton onSubmit={onSubmit}>
          Assign
        </SelectDialogSubmitButton>
      </DialogActions>
    </SelectDialog>
  );
};

export default AssignVerificationDialog;
